/**
 * `Sel: SelectionApi` (W1-09 state-store; contract src/state/types.ts, ARCHITECTURE.md §5.4).
 *
 * Pure helpers over the editor selection. A `Selection` is immutable data: every change builds new Sets and an
 * unchanged Set keeps its identity, so React selectors over one kind do not re-render when another kind changes.
 * Snapshots (`SelectionSnapshot`, ridden by every transaction) list ids in ascending order, so equal selections have
 * equal snapshots and hashes (coalescing keys). Segments are identified by the key they start at; `prune` drops a
 * segment whose start key is gone or has become the last key of its track (segment i runs from key i to key i+1).
 *
 * Besides the contract members, `Sel` offers `make`, `combine` (the select modes of the editor store), `count`,
 * `equals`, `has`, `holds`, `snapshotEquals` and `EMPTY_SNAPSHOT`.
 */

import type { ChartDoc, KeyId, TableName, TrackId } from '../core/model/types';
import type { SelectionSnapshot } from '../core/ops/types';
import { Beats } from '../core/time/beat';
import type { Beat } from '../core/time/types';
import type { Selection, SelectionApi } from './types';

/** The id-set members of a Selection. */
export type SelectionSetKey =
  'notes' | 'keys' | 'segments' | 'lines' | 'bpm' | 'markers' | 'effects' | 'videos' | 'groups';
export type SelectionSets = Omit<Selection, 'primary' | 'anchor'>;
export type SelectionPrimary = NonNullable<Selection['primary']>;
export type SelectionAnchor = NonNullable<Selection['anchor']>;
export type SelectMode = 'replace' | 'add' | 'toggle' | 'subtract';

/** Argument of `select` / `Sel.make`: some id sets plus optional primary and anchor. */
export type SelectionInput = Partial<SelectionSets> & {
  readonly primary?: Selection['primary'];
  readonly anchor?: Selection['anchor'];
};

export const SELECTION_SET_KEYS: readonly SelectionSetKey[] = Object.freeze([
  'notes',
  'keys',
  'segments',
  'lines',
  'bpm',
  'markers',
  'effects',
  'videos',
  'groups',
]);

/** The document table holding the records of each selection kind (segments are identified by their start key). */
export const SELECTION_TABLE: Readonly<Record<SelectionSetKey, TableName>> = Object.freeze({
  notes: 'notes',
  keys: 'keys',
  segments: 'keys',
  lines: 'lines',
  bpm: 'bpm',
  markers: 'markers',
  effects: 'effects',
  videos: 'videos',
  groups: 'groups',
});

const PRIMARY_SET: Readonly<Record<SelectionPrimary['kind'], SelectionSetKey>> = Object.freeze({
  note: 'notes',
  key: 'keys',
  segment: 'segments',
  line: 'lines',
  bpm: 'bpm',
  marker: 'markers',
  effect: 'effects',
});

const ANCHOR_TABLE: Readonly<Record<SelectionAnchor['kind'], TableName>> = Object.freeze({
  note: 'notes',
  key: 'keys',
});

/** Primary kind inferred for a set (videos and groups have no primary kind). */
const SET_PRIMARY: Readonly<Partial<Record<SelectionSetKey, SelectionPrimary['kind']>>> = Object.freeze({
  notes: 'note',
  keys: 'key',
  segments: 'segment',
  lines: 'line',
  bpm: 'bpm',
  markers: 'marker',
  effects: 'effect',
});

const EMPTY_SET: ReadonlySet<never> = new Set<never>();

/** The empty selection (plain data; safe to share: Sets are replaced, never mutated). */
const EMPTY_SELECTION: Selection = Object.freeze({
  notes: EMPTY_SET,
  keys: EMPTY_SET,
  segments: EMPTY_SET,
  lines: EMPTY_SET,
  bpm: EMPTY_SET,
  markers: EMPTY_SET,
  effects: EMPTY_SET,
  videos: EMPTY_SET,
  groups: EMPTY_SET,
  primary: null,
  anchor: null,
});

export const EMPTY_SNAPSHOT: SelectionSnapshot = Object.freeze({
  notes: [],
  keys: [],
  segments: [],
  lines: [],
  bpm: [],
  markers: [],
  effects: [],
  videos: [],
  groups: [],
  activeLine: null,
});

type MutableSets = { -readonly [K in SelectionSetKey]: ReadonlySet<number> };

function setsOf(s: SelectionSets): MutableSets {
  const out = {} as MutableSets;
  for (const k of SELECTION_SET_KEYS) out[k] = s[k];
  return out;
}

function sortedIds<T extends number>(set: ReadonlySet<T>): T[] {
  const out = [...set];
  out.sort((a, b) => a - b);
  return out;
}

function sameMembers(a: ReadonlySet<number>, b: ReadonlySet<number>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function primaryIn(sets: SelectionSets, p: Selection['primary']): boolean {
  return p !== null && (sets[PRIMARY_SET[p.kind]] as ReadonlySet<number>).has(p.id);
}

function samePrimary(
  a: Selection['primary'] | Selection['anchor'],
  b: Selection['primary'] | Selection['anchor'],
): boolean {
  if (a === b) return true;
  return a !== null && b !== null && a.kind === b.kind && a.id === b.id;
}

/** Total number of selected entities. */
function count(s: SelectionSets): number {
  let n = 0;
  for (const k of SELECTION_SET_KEYS) n += s[k].size;
  return n;
}

/** The single selected entity as a primary reference, or null (none, several, or a video/group). */
function soleItem(s: SelectionSets): Selection['primary'] {
  if (count(s) !== 1) return null;
  for (const k of SELECTION_SET_KEYS) {
    const set = s[k] as ReadonlySet<number>;
    if (set.size === 0) continue;
    const kind = SET_PRIMARY[k];
    if (kind === undefined) return null;
    for (const id of set) return { kind, id };
  }
  return null;
}

/** A Selection from sets; the primary must be selected (else it is inferred from a single item or dropped). */
function finish(
  sets: SelectionSets,
  primary: Selection['primary'] | undefined,
  anchor: Selection['anchor'],
): Selection {
  if (count(sets) === 0 && anchor === null) return EMPTY_SELECTION;
  const p = primary !== undefined && primary !== null && primaryIn(sets, primary) ? primary : soleItem(sets);
  return { ...sets, primary: p, anchor };
}

function make(input: SelectionInput): Selection {
  const sets = {} as MutableSets;
  for (const k of SELECTION_SET_KEYS) {
    const v = input[k] as ReadonlySet<number> | undefined;
    sets[k] = v === undefined || v.size === 0 ? EMPTY_SET : v;
  }
  return finish(sets as unknown as SelectionSets, input.primary ?? undefined, input.anchor ?? null);
}

function combineSet(a: ReadonlySet<number>, b: ReadonlySet<number> | undefined, mode: SelectMode): ReadonlySet<number> {
  if (mode === 'replace') {
    if (b === undefined || b.size === 0) return EMPTY_SET;
    return sameMembers(a, b) ? a : b;
  }
  if (b === undefined || b.size === 0) return a;
  const out = new Set(a);
  let changed = false;
  for (const x of b) {
    const present = out.has(x);
    if (mode === 'add' || (mode === 'toggle' && !present)) {
      if (!present) {
        out.add(x);
        changed = true;
      }
    } else if (present) {
      out.delete(x);
      changed = true;
    }
  }
  if (!changed) return a;
  return out.size === 0 ? EMPTY_SET : out;
}

/**
 * Applies a selection change with the editor's select modes:
 *  - replace: the given kinds become the selection (kinds not given are cleared);
 *  - add / subtract / toggle: per kind union / difference / symmetric difference with the current selection.
 * Primary: the given one when it ends up selected; otherwise the current one while still selected (not in replace
 * mode); otherwise the sole selected item. Anchor: the given one; otherwise kept (cleared in replace mode).
 * Unchanged Sets keep their identity; when nothing changes the current object is returned.
 */
function combine(cur: Selection, next: SelectionInput, mode: SelectMode = 'replace'): Selection {
  const sets = {} as MutableSets;
  let changed = false;
  for (const k of SELECTION_SET_KEYS) {
    const before = cur[k] as ReadonlySet<number>;
    const after = combineSet(before, next[k], mode);
    sets[k] = after;
    if (after !== before) changed = true;
  }
  const s = sets as unknown as SelectionSets;
  let primary: Selection['primary'];
  if (next.primary !== undefined && next.primary !== null && primaryIn(s, next.primary)) primary = next.primary;
  else if (mode !== 'replace' && primaryIn(s, cur.primary)) primary = cur.primary;
  else primary = soleItem(s);
  const anchor = next.anchor !== undefined ? next.anchor : mode === 'replace' ? null : cur.anchor;
  if (!changed && samePrimary(primary, cur.primary) && samePrimary(anchor, cur.anchor)) return cur;
  if (count(s) === 0 && anchor === null) return EMPTY_SELECTION;
  return { ...s, primary, anchor };
}

function toSnapshot(s: Selection, activeLine: SelectionSnapshot['activeLine']): SelectionSnapshot {
  return {
    notes: sortedIds(s.notes),
    keys: sortedIds(s.keys),
    segments: sortedIds(s.segments),
    lines: sortedIds(s.lines),
    bpm: sortedIds(s.bpm),
    markers: sortedIds(s.markers),
    effects: sortedIds(s.effects),
    videos: sortedIds(s.videos),
    groups: sortedIds(s.groups),
    activeLine,
  };
}

function fromSnapshot(snap: SelectionSnapshot): Selection {
  const sets = {} as MutableSets;
  for (const k of SELECTION_SET_KEYS) {
    const ids = snap[k] as readonly number[];
    sets[k] = ids.length === 0 ? EMPTY_SET : new Set(ids);
  }
  return finish(sets as unknown as SelectionSets, undefined, null);
}

function isEmpty(s: Selection): boolean {
  return count(s) === 0;
}

function tableHas(doc: ChartDoc, tbl: TableName, id: number): boolean {
  return (doc[tbl] as ReadonlyMap<unknown, unknown>).has(id);
}

/**
 * The selected segment starts that still start a segment: segment i runs from key i to key i+1 of its track, so the
 * start key must exist and must not be the last key of its track (a deleted following key ends the segment too).
 * One pass over the keys, only when segments are selected.
 */
function liveSegments(doc: ChartDoc, ids: ReadonlySet<KeyId>): Set<KeyId> {
  const live = new Set<KeyId>();
  if (ids.size === 0) return live;
  const latest = new Map<TrackId, Beat | null>();
  for (const id of ids) {
    const k = doc.keys.get(id);
    if (k !== undefined) latest.set(k.track, null);
  }
  if (latest.size === 0) return live;
  for (const k of doc.keys.values()) {
    if (!latest.has(k.track)) continue;
    const cur = latest.get(k.track) ?? null;
    if (cur === null || Beats.cmp(k.t, cur) > 0) latest.set(k.track, k.t);
  }
  for (const id of ids) {
    const k = doc.keys.get(id);
    if (k === undefined) continue;
    const last = latest.get(k.track) ?? null;
    if (last !== null && Beats.cmp(k.t, last) < 0) live.add(id);
  }
  return live;
}

/** True when every member of `set` exists in `tbl`. */
function allIn(doc: ChartDoc, tbl: TableName, set: ReadonlySet<number>): boolean {
  for (const id of set) if (!tableHas(doc, tbl, id)) return false;
  return true;
}

function prune(s: Selection, doc: ChartDoc): Selection {
  const sets = setsOf(s);
  let changed = false;
  for (const k of SELECTION_SET_KEYS) {
    const set = sets[k];
    if (set.size === 0) continue;
    if (k === 'segments') {
      const live = liveSegments(doc, set as ReadonlySet<KeyId>);
      if (live.size === set.size) continue;
      sets[k] = live.size === 0 ? EMPTY_SET : live;
      changed = true;
      continue;
    }
    const tbl = SELECTION_TABLE[k];
    if (allIn(doc, tbl, set)) continue;
    const kept = new Set<number>();
    for (const id of set) if (tableHas(doc, tbl, id)) kept.add(id);
    sets[k] = kept.size === 0 ? EMPTY_SET : kept;
    changed = true;
  }
  const anchorGone = s.anchor !== null && !tableHas(doc, ANCHOR_TABLE[s.anchor.kind], s.anchor.id);
  if (!changed && !anchorGone) return s;
  const next = sets as unknown as SelectionSets;
  const anchor = anchorGone ? null : s.anchor;
  return finish(next, primaryIn(next, s.primary) ? s.primary : undefined, anchor);
}

/**
 * Counts by kind for headers ("14 notes: 9 Tap, 3 Drag, 2 Hold"). Every key is present (zero when none):
 * `total`, `notes`, `tap`, `drag`, `flick`, `hold`, `fake`, `keys`, `segments`, `lines`, `bpm`, `markers`, `effects`,
 * `videos`, `groups`. Ids missing from the document (and segment starts that no longer start a segment) are not
 * counted.
 */
function summary(s: Selection, doc: ChartDoc): Readonly<Record<string, number>> {
  const out: Record<string, number> = {
    total: 0,
    notes: 0,
    tap: 0,
    drag: 0,
    flick: 0,
    hold: 0,
    fake: 0,
    keys: 0,
    segments: 0,
    lines: 0,
    bpm: 0,
    markers: 0,
    effects: 0,
    videos: 0,
    groups: 0,
  };
  for (const id of s.notes) {
    const note = doc.notes.get(id);
    if (note === undefined) continue;
    out.notes = (out.notes ?? 0) + 1;
    out[note.kind] = (out[note.kind] ?? 0) + 1;
    if (note.fake) out.fake = (out.fake ?? 0) + 1;
  }
  for (const k of SELECTION_SET_KEYS) {
    if (k === 'notes') continue;
    if (k === 'segments') {
      out[k] = liveSegments(doc, s.segments).size;
      continue;
    }
    const tbl = SELECTION_TABLE[k];
    let n = 0;
    for (const id of s[k] as ReadonlySet<number>) if (tableHas(doc, tbl, id)) n++;
    out[k] = n;
  }
  let total = 0;
  for (const k of SELECTION_SET_KEYS) total += out[k] ?? 0;
  out.total = total;
  return out;
}

function equals(a: Selection, b: Selection): boolean {
  if (a === b) return true;
  for (const k of SELECTION_SET_KEYS) if (!sameMembers(a[k], b[k])) return false;
  return samePrimary(a.primary, b.primary) && samePrimary(a.anchor, b.anchor);
}

function snapshotEquals(a: SelectionSnapshot, b: SelectionSnapshot): boolean {
  if (a === b) return true;
  if (a.activeLine !== b.activeLine) return false;
  for (const k of SELECTION_SET_KEYS) {
    const x = a[k] as readonly number[];
    const y = b[k] as readonly number[];
    if (x.length !== y.length) return false;
    const sx = [...x].sort((p, q) => p - q);
    const sy = [...y].sort((p, q) => p - q);
    for (let i = 0; i < sx.length; i++) if (sx[i] !== sy[i]) return false;
  }
  return true;
}

function has(s: Selection, kind: SelectionSetKey, id: number): boolean {
  return (s[kind] as ReadonlySet<number>).has(id);
}

/** True when the item a primary or anchor reference names is selected. */
function holds(s: Selection, ref: SelectionPrimary | SelectionAnchor): boolean {
  return (s[PRIMARY_SET[ref.kind]] as ReadonlySet<number>).has(ref.id);
}

export interface SelectionApiExt extends SelectionApi {
  readonly EMPTY_SNAPSHOT: SelectionSnapshot;
  /** A Selection from some id sets (primary inferred from a single item when not given or not selected). */
  make(input: SelectionInput): Selection;
  /** The editor's select modes (see `combine` above). */
  combine(cur: Selection, next: SelectionInput, mode?: SelectMode): Selection;
  /** Total number of selected entities. */
  count(s: Selection): number;
  equals(a: Selection, b: Selection): boolean;
  has(s: Selection, kind: SelectionSetKey, id: number): boolean;
  /** True when the item a primary or anchor reference names is selected. */
  holds(s: Selection, ref: SelectionPrimary | SelectionAnchor): boolean;
  /** Same ids (order-insensitive) and the same active line. */
  snapshotEquals(a: SelectionSnapshot, b: SelectionSnapshot): boolean;
}

export const Sel: SelectionApiExt = {
  EMPTY: EMPTY_SELECTION,
  EMPTY_SNAPSHOT,
  toSnapshot,
  fromSnapshot,
  isEmpty,
  prune,
  summary,
  make,
  combine,
  count,
  equals,
  has,
  holds,
  snapshotEquals,
};
